import {
  FACET_NAME_TO_DISPLAY_NAME,
  FACET_NAME_TO_QUERY_PARAM,
  PUBLICATION_YEAR_GROUPS,
  FACET_NAME_DISPLAY_CODE,
} from "util/facetedSearchMappings";

import {
  assoc,
  comparator,
  curry,
  defaultTo,
  eqBy,
  filter,
  find,
  findIndex,
  flip,
  head,
  intersection,
  isEmpty,
  isNil,
  map,
  mergeWith,
  mergeWithKey,
  pipe,
  prop,
  propEq,
  reduce,
  reject,
  sort,
  tail,
  union,
  unionWith,
} from "ramda";

const sortTerms = sort((a, b) => a.label.localeCompare(b.label));

const defaultToFalse = defaultTo(false);

const findYearGroupIndex = (group, groups) =>
  findIndex(propEq("name", group.name), groups);

const byYearGroupIndex = comparator((a, b) => {
  const aIndex = findYearGroupIndex(a, PUBLICATION_YEAR_GROUPS);
  const bIndex = findYearGroupIndex(b, PUBLICATION_YEAR_GROUPS);
  return aIndex < bIndex;
});

export const sortPublicationYearGroups = sort(byYearGroupIndex);

export const updateTermCounts = curry((newTerms, oldTerms) =>
  map(oldTerm => {
    const newTerm = find(propEq("code", oldTerm.code), newTerms);
    const newCount = isNil(newTerm) ? 0 : newTerm.count;

    return assoc("count", newCount, oldTerm);
  }, oldTerms),
);

export const updateFacetTermCounts = curry((newFacets, currentFacets) =>
  map(oldFacet => {
    const newFacet = newFacets[oldFacet.name];
    const newTerms = isNil(newFacet) ? [] : newFacet.terms;
    return assoc("terms", updateTermCounts(newTerms)(oldFacet.terms), oldFacet);
  }, currentFacets),
);

const mergeFacets = mergeWithKey((key, oldFacetProp, newFacetProp) => {
  if (key === "terms") {
    const unitedTerms = pipe(
      unionWith(eqBy(prop("code")), oldFacetProp),
      sortTerms,
    )(newFacetProp);

    return updateTermCounts(newFacetProp)(unitedTerms);
  } else {
    return newFacetProp;
  }
});

export const mergeFacetCollections = mergeWith(mergeFacets);

const getPublicationYearTerms = facets =>
  isNil(facets.contentDatePublished) ? [] : facets.contentDatePublished.terms;

const getPublicationYearsFromTerms = map(t => parseInt(t.label, 10));

export const unionPublicationYearGroups = unionWith(eqBy(prop("name")));
export const unionYearTerms = unionWith(eqBy(prop("code")));

export const getYearFromYearTerm = yearTerm => parseInt(yearTerm.label, 10);

export const isYearInGroup = curry(
  (group, year) =>
    (group.start === void 0 || year >= group.start) &&
    (group.end === void 0 || year < group.end),
);

export const isYearTermInGroup = curry((group, yearTerm) =>
  isYearInGroup(group, getYearFromYearTerm(yearTerm)),
);

export const filterYearTermsInGroup = curry((group, yearTerms) =>
  filter(isYearTermInGroup(group), yearTerms),
);

export const isGroupForYear = flip(isYearInGroup);

export const findGroupForYear = curry((year, groups) =>
  find(isGroupForYear(year), groups),
);
export const findGroupForYearTerm = curry((yearTerm, groups) =>
  findGroupForYear(getYearFromYearTerm(yearTerm), groups),
);

export const getGroupsForYears = curry((allGroups, years) =>
  reduce(
    (groups, year) =>
      unionPublicationYearGroups(groups, [findGroupForYear(year, allGroups)]),
    [],
    years,
  ),
);

export const withoutYearTerm = curry((yearTerm, yearTerms) =>
  reject(propEq("code", yearTerm.code), yearTerms),
);
export const withoutGroup = curry((group, groups) =>
  reject(propEq("name", group.name), groups),
);

export const rejectYearTermsInGroup = curry((group, yearTerms) =>
  reject(isYearTermInGroup(group), yearTerms),
);

export const getPublicationYearGroups = pipe(
  getPublicationYearTerms,
  getPublicationYearsFromTerms,
  getGroupsForYears(PUBLICATION_YEAR_GROUPS),
);

export const findMediaItems = curry((mediaList, mediaIds) =>
  map(id => find(propEq("id", id), mediaList), mediaIds),
);

const getParentModuleIds = media =>
  !isNil(media.parentModules) && !isEmpty(media.parentModules)
    ? map(m => m.id, media.parentModules)
    : [];

const getAllParentModuleIds = map(m => getParentModuleIds(m));

const reduceParentModuleIds = (mediaItems, reducer) => {
  const all = getAllParentModuleIds(mediaItems);
  return all.length > 1
    ? reduce(reducer, head(all), tail(all))
    : all.length > 0
    ? head(all)
    : [];
};

export const getIntersectionOfParentModuleIds = mediaItems =>
  reduceParentModuleIds(mediaItems, intersection);

export const getUnionOfParentModuleIds = mediaItems =>
  reduceParentModuleIds(mediaItems, union);

export const normalizeFacetResults = pipe(
  map(f => ({
    name: f.fieldName,
    queryParam: FACET_NAME_TO_QUERY_PARAM[f.fieldName],
    displayName: FACET_NAME_TO_DISPLAY_NAME[f.fieldName],
    terms: sortTerms(f.facetValues),
    displayCode: defaultToFalse(FACET_NAME_DISPLAY_CODE[f.fieldName]),
  })),
  filter(f => !isEmpty(f.terms)),
);

export const normalizeAppliedFacets = reduce(
  (acc, facet) => ({
    ...acc,
    [facet.name]: { ...facet },
  }),
  {},
);
